import { findIndex } from 'lodash';
import { useState } from 'react';
import {
  Button,
  Stack,
  Typography,
  Box,
  Popover,
  FormControl,
  Select,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import Iconify from '../iconify';
import { getTimeOptions, formatBreakTime } from '../../utils/formatTime';
import { WorkingHoursSkeleton } from '../skeleton';
import palette from 'src/theme/palette';

const DAYS_IN_WEEK = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};

export default function WorkingHoursWithCopy({ availability, setAvailability }) {
  const [popover, setPopover] = useState('');
  const [anchorBreak, setAnchorBreak] = useState(null);
  const [copyToAnchor, setCopyToAnchor] = useState(null);
  const [selectedDayForCopy, setSelectedDayForCopy] = useState(null);
  const [selectedTargetDays, setSelectedTargetDays] = useState([]);
  const [dirtyBreak, setDirtyBreak] = useState({});

  const options = getTimeOptions();
  const timeOptionsSelect = options.map((option, index) => (
    <option key={index} value={option.value}>
      {option.label}
    </option>
  ));

  function handleShowPopover(event, weekDay) {
    setPopover(weekDay);
    setAnchorBreak(event.currentTarget);
  }

  function handleClosePopover() {
    setPopover('');
    setAnchorBreak(null);
  }

  const handleChangeDateOnDay = (event, day) => {
    const availabilityOnDayIndex = findIndex(availability, { day });
    const updatedAvailability = [...availability];
    updatedAvailability[availabilityOnDayIndex][event.target.name] = event.target.value;
    
    // If one option is set to "off", set the other to "off" as well
    if (event.target.value === 'off') {
      const otherField = event.target.name === 'startDate' ? 'endDate' : 'startDate';
      updatedAvailability[availabilityOnDayIndex][otherField] = 'off';
    }
    
    setAvailability(updatedAvailability);
  };

  const handleChangeBreak = (event, day) => {
    const mutatedBreaks = { ...dirtyBreak };
    const isValidDate = event.target.value !== 'off';

    if (!isValidDate) return;

    if (mutatedBreaks[day]) {
      mutatedBreaks[day] = {
        ...mutatedBreaks[day],
        [event.target.name]: event.target.value,
      };
    } else {
      mutatedBreaks[day] = {
        [event.target.name]: event.target.value,
      };
    }
    setDirtyBreak(mutatedBreaks);
  };

  const handleAddBreak = (event, day) => {
    if (!availability.length) return;

    const newBreak = dirtyBreak[day];
    if (!newBreak) return;

    const mutatedAvailability = [...availability];

    const availabilityOnDayIndex = findIndex(mutatedAvailability, {
      day: Number(day),
    });
    if (availabilityOnDayIndex !== -1) {
      mutatedAvailability[availabilityOnDayIndex].breaks.push(newBreak);
    }

    setAvailability(mutatedAvailability);
    setDirtyBreak({});

    handleClosePopover(event, '0');
  };

  const handleRemoveBreak = ({ day, breakIndex }) => {
    const mutatedAvailability = [...availability];
    const availabilityOnDayIndex = findIndex(mutatedAvailability, {
      day: Number(day),
    });

    if (availabilityOnDayIndex === -1) return;

    mutatedAvailability[availabilityOnDayIndex].breaks.splice(breakIndex, 1);
    setAvailability(mutatedAvailability);
  };

  const handleCancelAddBreak = (event, day) => {
    delete dirtyBreak[day];
    handleClosePopover(event, '0');
  };

  // Copy functionality
  const handleShowCopyPopover = (event, dayData) => {
    if (dayData.startDate === 'off' || dayData.endDate === 'off') return;
    
    setCopyToAnchor(event.currentTarget);
    setSelectedDayForCopy(dayData);
  };

  const handleCloseCopyPopover = () => {
    setCopyToAnchor(null);
    setSelectedDayForCopy(null);
    setSelectedTargetDays([]);
  };

  const handleToggleTargetDay = (targetDay) => {
    if (!selectedDayForCopy || selectedDayForCopy.day === targetDay) return;
    
    setSelectedTargetDays(prev => {
      if (prev.includes(targetDay)) {
        return prev.filter(day => day !== targetDay);
      } else {
        return [...prev, targetDay];
      }
    });
  };

  const handleApplyCopy = () => {
    if (!selectedDayForCopy || !selectedTargetDays.length) return;
    
    const updatedAvailability = [...availability];
    const sourceDay = selectedDayForCopy;
    
    selectedTargetDays.forEach(targetDay => {
      const targetDayIndex = updatedAvailability.findIndex(d => d.day === targetDay);
      
      if (targetDayIndex !== -1) {
        updatedAvailability[targetDayIndex] = {
          ...updatedAvailability[targetDayIndex],
          startDate: sourceDay.startDate,
          endDate: sourceDay.endDate,
          breaks: [...sourceDay.breaks]
        };
      }
    });
    
    setAvailability(updatedAvailability);
    handleCloseCopyPopover();
  };

  return (
    <Stack>
      {!availability.length &&
        [...Array(7)].map((_, index) => <WorkingHoursSkeleton key={index} />)}
      {availability.map(({ day, startDate, endDate, breaks }) => {
        const isOff = startDate === 'off' || endDate === 'off';
        
        return (
          <FormControl fullWidth key={day}>
            <Typography>{DAYS_IN_WEEK[day]}</Typography>
            <Box
              rowGap={1}
              columnGap={1}
              display="grid"
              sx={{ mt: 2, mb: 2 }}
              gridTemplateColumns={{
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(2, 1fr) 102px 102px',
              }}
            >
              <Select
                native
                value={startDate}
                size="small"
                onChange={(event) => handleChangeDateOnDay(event, day)}
                name="startDate"
              >
                <option value="off">
                  <em>Off</em>
                </option>
                {timeOptionsSelect}
              </Select>

              <Select
                native
                value={endDate}
                size="small"
                onChange={(event) => handleChangeDateOnDay(event, day)}
                name="endDate"
              >
                <option value="off">
                  <em>Off</em>
                </option>
                {timeOptionsSelect}
              </Select>

              <Button
                aria-describedby="0"
                sx={{
                  fontSize: '14px',
                  backgroundColor: palette.primary.lighter,
                  color: palette.primary.main,
                }}
                onClick={(event) => handleShowPopover(event, day)}
              >
                Add Break
              </Button>

              <Button
                disabled={isOff}
                sx={{
                  fontSize: '14px',
                  backgroundColor: isOff ? 'grey.100' : 'grey.100',
                  color: isOff ? 'grey.400' : 'grey.600',
                  minWidth: 'auto',
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  p: 1,
                  '&:hover': {
                    backgroundColor: isOff ? 'grey.100' : 'grey.200',
                  }
                }}
                onClick={(event) => handleShowCopyPopover(event, { day, startDate, endDate, breaks })}
              >
                <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 9.18164H11C9.89543 9.18164 9 10.0771 9 11.1816V20.1816C9 21.2862 9.89543 22.1816 11 22.1816H20C21.1046 22.1816 22 21.2862 22 20.1816V11.1816C22 10.0771 21.1046 9.18164 20 9.18164Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5 15.1816H4C3.46957 15.1816 2.96086 14.9709 2.58579 14.5959C2.21071 14.2208 2 13.7121 2 13.1816V4.18164C2 3.65121 2.21071 3.1425 2.58579 2.76743C2.96086 2.39235 3.46957 2.18164 4 2.18164H13C13.5304 2.18164 14.0391 2.39235 14.4142 2.76743C14.7893 3.1425 15 3.65121 15 4.18164V5.18164" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Button>

              <Popover
                anchorEl={anchorBreak}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
                onClose={(event) => handleClosePopover(event, day)}
                open={Boolean(popover === day)}
              >
                <FormControl fullWidth>
                  <Typography sx={{ p: 2 }}>Add Break</Typography>
                  <Box
                    rowGap={3}
                    columnGap={1}
                    display="grid"
                    sx={{ padding: '2px 11px 11px 11px' }}
                    gridTemplateColumns={{
                      xs: 'repeat(1, 1fr)',
                      sm: 'repeat(2, 1fr)',
                    }}
                  >
                    <Select
                      native
                      defaultValue={options[0]}
                      size="small"
                      name="startDate"
                      onChange={(event) => handleChangeBreak(event, day)}
                    >
                      <option value="off">
                        <em>Off</em>
                      </option>
                      {timeOptionsSelect}
                    </Select>
                    <Select
                      native
                      defaultValue={options[0]}
                      size="small"
                      name="endDate"
                      onChange={(event) => handleChangeBreak(event, day)}
                    >
                      <option value="off">
                        <em>Off</em>
                      </option>
                      {timeOptionsSelect}
                    </Select>
                  </Box>
                  <Box
                    rowGap={1}
                    columnGap={1}
                    display="grid"
                    sx={{ padding: '11px' }}
                    gridTemplateColumns={{
                      xs: 'repeat(1, 1fr)',
                      sm: 'repeat(2, 1fr)',
                    }}
                  >
                    <Button
                      onClick={(event) => handleCancelAddBreak(event, day)}
                      variant="outlined"
                      size="small"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={(event) => handleAddBreak(event, day)}
                      variant="contained"
                      size="small"
                    >
                      Add
                    </Button>
                  </Box>
                </FormControl>
              </Popover>
            </Box>

            {breaks && breaks.length > 0 && (
              <Stack sx={{ ml: 2, mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Breaks:
                </Typography>
                {breaks.map((breakTime, breakIndex) => (
                  <Box
                    key={breakIndex}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 0.5,
                    }}
                  >
                    <Typography variant="body2">
                      {formatBreakTime(breakTime.startDate)} - {formatBreakTime(breakTime.endDate)}
                    </Typography>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleRemoveBreak({ day, breakIndex })}
                    >
                      <Iconify icon="eva:trash-2-outline" />
                    </Button>
                  </Box>
                ))}
              </Stack>
            )}
          </FormControl>
        );
      })}

      {/* Copy To Popover */}
      <Popover
        open={Boolean(copyToAnchor)}
        anchorEl={copyToAnchor}
        onClose={handleCloseCopyPopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Copy to:
          </Typography>
          <Stack spacing={1}>
            {availability.map((day) => (
              <FormControlLabel
                key={day.day}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedTargetDays.includes(day.day)}
                    onChange={() => handleToggleTargetDay(day.day)}
                    disabled={selectedDayForCopy?.day === day.day}
                  />
                }
                label={DAYS_IN_WEEK[day.day]}
                sx={{ m: 0 }}
              />
            ))}
            <Button 
              variant="contained"
              size="small"
              onClick={handleApplyCopy}
              sx={{ 
                mt: 2,
                bgcolor: 'success.main',
                '&:hover': {
                  bgcolor: 'success.dark'
                }
              }}
            >
              Apply
            </Button>
          </Stack>
        </Box>
      </Popover>
    </Stack>
  );
}